import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Badge } from '../../../components/ui/Badge';
import { useAgingReport, usePDDReport } from '../../../hooks/useCredit';

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value / 100);
};

const formatDate = (date: string) => {
  return new Date(date).toLocaleDateString('pt-BR');
};

const formatPercent = (value: number) => {
  return `${(value * 100).toFixed(2)}%`;
};

export function CreditReports() {
  const [reportDate, setReportDate] = useState(new Date().toISOString().split('T')[0]);
  const [activeTab, setActiveTab] = useState<'aging' | 'pdd'>('aging');

  const { data: agingReport, isLoading: agingLoading } = useAgingReport(reportDate);
  const { data: pddReport, isLoading: pddLoading } = usePDDReport(reportDate);

  const handleExport = (format: 'csv' | 'pdf') => {
    // TODO: Implementar exportação
    alert(`Exportando relatório em ${format.toUpperCase()}...`);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Relatórios de Crédito</h1>
        <div className="flex gap-2">
          <Input
            type="date"
            value={reportDate}
            onChange={(e) => setReportDate(e.target.value)}
            className="w-40"
          />
          <Button variant="secondary" onClick={() => handleExport('csv')}>
            Exportar CSV
          </Button>
          <Button variant="secondary" onClick={() => handleExport('pdf')}>
            Exportar PDF
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b">
        <button
          className={`px-4 py-2 font-medium ${
            activeTab === 'aging'
              ? 'border-b-2 border-blue-500 text-blue-600'
              : 'text-gray-500'
          }`}
          onClick={() => setActiveTab('aging')}
        >
          Relatório de Aging
        </button>
        <button
          className={`px-4 py-2 font-medium ${
            activeTab === 'pdd'
              ? 'border-b-2 border-blue-500 text-blue-600'
              : 'text-gray-500'
          }`}
          onClick={() => setActiveTab('pdd')}
        >
          Provisão (PDD)
        </button>
      </div>

      {/* Aging Report */}
      {activeTab === 'aging' && (
        <div className="space-y-6">
          {agingLoading ? (
            <div className="text-center py-8">Carregando relatório...</div>
          ) : !agingReport ? (
            <div className="text-center py-8 text-gray-500">
              Nenhum dado disponível para a data selecionada.
            </div>
          ) : (
            <>
              {/* Resumo */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-sm text-gray-500">Em Dia</p>
                    <p className="text-xl font-bold text-green-600">
                      {formatCurrency(agingReport.current.amount)}
                    </p>
                    <p className="text-sm text-gray-500">
                      {agingReport.current.count} contratos
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-sm text-gray-500">1-30 dias</p>
                    <p className="text-xl font-bold text-yellow-600">
                      {formatCurrency(agingReport.days1to30.amount)}
                    </p>
                    <p className="text-sm text-gray-500">
                      {agingReport.days1to30.count} contratos
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-sm text-gray-500">31-60 dias</p>
                    <p className="text-xl font-bold text-orange-600">
                      {formatCurrency(agingReport.days31to60.amount)}
                    </p>
                    <p className="text-sm text-gray-500">
                      {agingReport.days31to60.count} contratos
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-sm text-gray-500">61-90 dias</p>
                    <p className="text-xl font-bold text-red-500">
                      {formatCurrency(agingReport.days61to90.amount)}
                    </p>
                    <p className="text-sm text-gray-500">
                      {agingReport.days61to90.count} contratos
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-sm text-gray-500">90+ dias</p>
                    <p className="text-xl font-bold text-red-700">
                      {formatCurrency(agingReport.days90plus.amount)}
                    </p>
                    <p className="text-sm text-gray-500">
                      {agingReport.days90plus.count} contratos
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Gráfico de Barras Visual */}
              <Card>
                <CardHeader>
                  <CardTitle>Distribuição por Faixa de Atraso</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {[
                      { label: 'Em Dia', data: agingReport.current, color: 'bg-green-500' },
                      { label: '1-30 dias', data: agingReport.days1to30, color: 'bg-yellow-500' },
                      { label: '31-60 dias', data: agingReport.days31to60, color: 'bg-orange-500' },
                      { label: '61-90 dias', data: agingReport.days61to90, color: 'bg-red-500' },
                      { label: '90+ dias', data: agingReport.days90plus, color: 'bg-red-700' },
                    ].map((item) => {
                      const percentage = (item.data.amount / agingReport.totalAmount) * 100;
                      return (
                        <div key={item.label} className="space-y-1">
                          <div className="flex justify-between text-sm">
                            <span>{item.label}</span>
                            <span>
                              {formatCurrency(item.data.amount)} ({percentage.toFixed(1)}%)
                            </span>
                          </div>
                          <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className={`h-full ${item.color}`}
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="mt-6 pt-4 border-t">
                    <div className="flex justify-between font-medium">
                      <span>Total da Carteira</span>
                      <span>{formatCurrency(agingReport.totalAmount)}</span>
                    </div>
                    <div className="flex justify-between text-sm text-gray-500">
                      <span>Total de Contratos</span>
                      <span>{agingReport.totalCount}</span>
                    </div>
                    <div className="flex justify-between text-sm text-gray-500">
                      <span>Data de Referência</span>
                      <span>{formatDate(agingReport.reportDate)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      )}

      {/* PDD Report */}
      {activeTab === 'pdd' && (
        <div className="space-y-6">
          {pddLoading ? (
            <div className="text-center py-8">Carregando relatório...</div>
          ) : !pddReport ? (
            <div className="text-center py-8 text-gray-500">
              Nenhum dado disponível para a data selecionada.
            </div>
          ) : (
            <>
              {/* Resumo PDD */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-sm text-gray-500">Carteira Total</p>
                    <p className="text-2xl font-bold">
                      {formatCurrency(pddReport.totalPortfolio)}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-sm text-gray-500">Provisão Total (PDD)</p>
                    <p className="text-2xl font-bold text-red-600">
                      {formatCurrency(pddReport.totalProvision)}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-sm text-gray-500">% Provisão</p>
                    <p className="text-2xl font-bold">
                      {formatPercent(pddReport.provisionRate)}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Tabela de Provisão por Faixa */}
              <Card>
                <CardHeader>
                  <CardTitle>Provisão por Faixa de Atraso</CardTitle>
                </CardHeader>
                <CardContent>
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3">Faixa</th>
                        <th className="text-right py-3">Saldo</th>
                        <th className="text-center py-3">Taxa PDD</th>
                        <th className="text-right py-3">Provisão</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pddReport.buckets.map((bucket) => (
                        <tr key={bucket.bucket} className="border-b">
                          <td className="py-3">
                            <Badge
                              variant={
                                bucket.bucket === 'current'
                                  ? 'success'
                                  : bucket.bucket === '90+'
                                  ? 'error'
                                  : 'default'
                              }
                            >
                              {bucket.bucket === 'current'
                                ? 'Em Dia'
                                : bucket.bucket === '1-30'
                                ? '1-30 dias'
                                : bucket.bucket === '31-60'
                                ? '31-60 dias'
                                : bucket.bucket === '61-90'
                                ? '61-90 dias'
                                : '90+ dias'}
                            </Badge>
                          </td>
                          <td className="text-right py-3">
                            {formatCurrency(bucket.amount)}
                          </td>
                          <td className="text-center py-3">
                            {formatPercent(bucket.provisionRate)}
                          </td>
                          <td className="text-right py-3 font-medium">
                            {formatCurrency(bucket.provision)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="font-bold">
                        <td className="py-3">Total</td>
                        <td className="text-right py-3">
                          {formatCurrency(pddReport.totalPortfolio)}
                        </td>
                        <td className="text-center py-3">
                          {formatPercent(pddReport.provisionRate)}
                        </td>
                        <td className="text-right py-3">
                          {formatCurrency(pddReport.totalProvision)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </CardContent>
              </Card>

              {/* Informações Adicionais */}
              <Card>
                <CardHeader>
                  <CardTitle>Informações do Relatório</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Data de Referência</p>
                      <p className="font-medium">{formatDate(pddReport.reportDate)}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Metodologia</p>
                      <p className="font-medium">Resolução CMN 2.682/99</p>
                    </div>
                  </div>

                  <div className="mt-4 p-4 bg-yellow-50 rounded-lg">
                    <p className="text-sm text-yellow-800">
                      <strong>Nota:</strong> A provisão é calculada com base nas taxas
                      mínimas estabelecidas pela Resolução CMN 2.682/99. Taxas
                      aplicadas: Em dia (0,5%), 1-30 dias (1%), 31-60 dias (3%),
                      61-90 dias (10%), 90+ dias (30-100%).
                    </p>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default CreditReports;
